import React, { useMemo } from 'react';

import { Card, Badge, BadgePriority } from '@wix/wix-ui-tpa/cssVars';
import { MessageType } from '@wix/ambassador-innovation-widget-v1-message/types';
import { useTranslation } from '@wix/yoshi-flow-editor';

import {
  senderIsAssistant,
  senderIsOwner,
  senderIsVisitor,
} from '@/utils/sender';
import { useAppSelector } from '@/store';
import { messagesModel, selectFlags, selectOneChatSettings } from '@/features';
import { formatMessageCreatedLabel } from '@/utils/formatMessageCreatedLabel';

import settingsParams from 'AiAssistantWidget/settingsParams';
import { useWidgetSettings } from 'AiAssistantWidget/Widget/contexts';

import { ChatMessageWrapper } from '../ChatMessageWrapper';
import { ChatMessagePayload } from '../ChatMessagePayload';
import { ContactForm } from '../ContactForm';
import { ContactFormDataHooks } from '../ContactForm/ContactFormDataHooks';
import { Markdown } from '../Markdown';
import { Avatar } from '../Avatar/Avatar';

import { st, classes, cssStates } from './ChatMessage.st.css';
import { VisitorOptions } from './VisitorOptions';

export interface ChatMessageProps {
  message?: messagesModel.Message;
  last?: boolean;
}

export const ChatMessage = ({ message, last = false }: ChatMessageProps) => {
  const { t, i18n } = useTranslation();
  const settings = useWidgetSettings();

  const isContact = useAppSelector(selectFlags.isContact);
  const contactFormIntroMessage = useAppSelector(
    selectOneChatSettings.contactFormIntroMessage,
  );
  const contactFormSuccessMessage = useAppSelector(
    selectOneChatSettings.contactFormSuccessMessage,
  );

  const isStaffMessage = !senderIsVisitor(message?.sender);

  const createdLabel = useMemo(() => {
    const ms = message?.createdDate;
    if (ms === undefined || ms <= 0) {
      return '';
    }
    return formatMessageCreatedLabel(ms, i18n.language);
  }, [message?.createdDate, i18n.language]);

  const aria = useMemo(() => {
    if (!message) {
      return {};
    }
    const { sender } = message;

    const messageText = messagesModel.hasBodyWithText(message)
      ? message.body.mainText
      : '';

    const timeSuffix = createdLabel ? `, ${createdLabel}` : '';

    if (senderIsAssistant(sender)) {
      return {
        'aria-label': `${t('app.widget.ariallabel.answer', {
          name: settings.get(settingsParams.textHeader),
        })}: ${messageText}${timeSuffix}`,
      };
    }

    if (senderIsOwner(sender)) {
      return {
        'aria-label': `${t(
          'app.widget.ariallabel.intervention',
        )}: ${messageText}${timeSuffix}`,
      };
    }

    if (senderIsVisitor(sender)) {
      return {
        'aria-label': `${t(
          'app.widget.ariallabel.question',
        )}: ${messageText}${timeSuffix}`,
      };
    }
    return {};
  }, [message, t, settings, createdLabel]);

  const hasMainText = !!message?.body?.mainText?.trim();
  const hasFooterText = !!message?.body?.footerText?.trim();
  const hasAdditionalNonEngineData =
    !!message?.body?.additionalData?.length &&
    !message.body.additionalData.every(
      (data) =>
        !!data?.assistantAnswerStatus && Object.keys(data ?? {}).length === 1,
    );
  const hasVisitorOptions =
    !!message &&
    (messagesModel.hasVisitorOptions(message) ||
      messagesModel.hasVisitorOptionsFields(message));
  const hasContactForm = message?.messageType === MessageType.CONTACT_FORM;
  const hasSubmittedContactForm =
    message?.messageType === MessageType.CONTACT_FORM_SUBMITTED;
  const contactFormIntro =
    contactFormIntroMessage ?? t('app.widget.form.beforeText') ?? '';
  const contactFormSuccess =
    contactFormSuccessMessage ?? t('app.widget.form.submitResponse') ?? '';
  const shouldShowContactFormIntro =
    hasContactForm && message?.body?.mainText !== contactFormIntro;

  if (
    !hasMainText &&
    !hasAdditionalNonEngineData &&
    !hasFooterText &&
    !hasVisitorOptions &&
    !hasContactForm &&
    !hasSubmittedContactForm
  ) {
    return null;
  }

  return (
    <ChatMessageWrapper
      className={last ? st(classes.lastMessage) : ''}
      {...aria}
    >
      <Card
        className={st(
          classes.root,
          cssStates({
            aiAnswer: isStaffMessage,
            applyAiAnswerShadow:
              isStaffMessage &&
              settings.get(
                settingsParams.applyAssistantMessageShadow,
                'primary',
              ),
            userQuestion: !isStaffMessage,
            applyUserQuestionBackground:
              !isStaffMessage &&
              settings.get(
                settingsParams.applyVisitorMessageBackground,
                'primary',
              ),
            applyUserQuestionShadow:
              !isStaffMessage &&
              settings.get(settingsParams.applyVisitorMessageShadow, 'primary'),
          }),
        )}
      >
        <Card.Container className={st(classes.container)}>
          <div
            className={st(
              classes.icon,
              cssStates({ isUserMessage: !isStaffMessage }),
            )}
          >
            <Avatar
              message={message}
              memberIconClassname={st(classes.memberAvatar)}
            />
          </div>
          <div className={st(classes.content)}>
            {senderIsOwner(message?.sender) && (
              <div className={st(classes.badgeContainer)}>
                <Badge priority={BadgePriority.secondary}>
                  {t('app.widget.badge.stuffRespond')}
                </Badge>
              </div>
            )}
            {message && ( // TODO: make better matching message types
              <>
                <div className={st(classes.contentStack)}>
                  {message.messageType ===
                    MessageType.CONTACT_FORM_SUBMITTED && (
                    <div data-hook={ContactFormDataHooks.SUBMITTED_MESSAGE}>
                      <Markdown
                        className={st(classes.text)}
                        text={contactFormSuccess}
                      />
                    </div>
                  )}

                  {messagesModel.hasBodyWithText(message) && (
                    <Markdown
                      className={st(classes.text)}
                      text={message.body.mainText}
                    />
                  )}

                  {shouldShowContactFormIntro && (
                    <Markdown
                      className={st(classes.text)}
                      text={contactFormIntro}
                    />
                  )}

                  {message.body?.additionalData && (
                    <ChatMessagePayload
                      additionalData={message.body.additionalData}
                    />
                  )}

                  {message.body?.footerText && (
                    <Markdown
                      className={st(classes.suffixText)}
                      text={message.body.footerText}
                    />
                  )}

                  {message.messageType === MessageType.CONTACT_FORM &&
                    !isContact && <ContactForm formId={message.id} />}

                  <VisitorOptions message={message} />
                </div>

                {createdLabel ? (
                  <time
                    className={st(classes.time)}
                    dateTime={
                      message.createdDate
                        ? new Date(message.createdDate).toISOString()
                        : undefined
                    }
                  >
                    {createdLabel}
                  </time>
                ) : null}
              </>
            )}
          </div>
        </Card.Container>
      </Card>
    </ChatMessageWrapper>
  );
};
